import React, { useState, useEffect } from 'react';
import axios from "axios";
import config from "../../common/Config";
import { postGetPriceVariation } from "../../../utils/priceVariationApi";
import { encryptPayload, normalizeEncryptedApiResponse, plainObjectFromFormData } from "../../../utils/customerCrypto";
import { getCompanySettings, resolveCustomerApiEncrypt } from "../../../utils/companySettingsCache";
import { Box, Grid2, Typography, TextField, Button, Dialog, DialogTitle, DialogContent, DialogActions, Paper, Snackbar, Alert, InputAdornment, TablePagination, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Skeleton, IconButton } from "@mui/material";
import { Iconify } from '../../Minimal/src/components/iconify/iconify';
import '../../common/commonStyles.css';

const HEADERS = ["Date", "Percentage 2", "Percentage 3", "Percentage 4", "Percentage 5", "Percentage 6", "AddBy"];

const Products_Price_Variation = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInputValue, setSearchInputValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [totalRecords, setTotalRecords] = useState(0);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const accessToken = localStorage.getItem('accessToken');
  const userId = localStorage.getItem('userId');
  const token = localStorage.getItem("token");

  const handleSearchChange = (e) => setSearchInputValue(e.target.value);

  const handleChangePage = (event, newPage) => setPage(newPage);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  useEffect(() => {
    if (accessToken === null) {
      window.location.href = `${config.BASE_URL}/login`;
      return;
    }
  }, [accessToken]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(0);
      setSearchTerm(searchInputValue.trim());
    }, 300);
    return () => clearTimeout(t);
  }, [searchInputValue]);

  useEffect(() => {
    if (!accessToken) return;
    let active = true;
    setLoading(true);
    (async () => {
      try {
        const json = await postGetPriceVariation(`${config.API_URL}/get_price_variation`, token, {
          draw: page + 1,
          start: page * rowsPerPage,
          length: rowsPerPage,
          searchValue: searchTerm,
        });
        if (!active) return;
        const safe = json || {};
        const data = Array.isArray(safe.data) ? safe.data : [];
        setRows(data);
        const total = Number(safe.recordsFiltered ?? safe.recordsTotal) || data.length;
        setTotalRecords(total);
      } catch {
        if (!active) return;
        setRows([]);
        setTotalRecords(0);
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => { active = false; };
  }, [accessToken, page, rowsPerPage, searchTerm, token]);

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [percentage1, setPercentage1] = useState('');
  const [percentage2, setPercentage2] = useState('');
  const [percentage3, setPercentage3] = useState('');
  const [percentage4, setPercentage4] = useState('');
  const [percentage5, setPercentage5] = useState('');

  const [calculatedPrice1, setCalculatedPrice1] = useState(0);
  const [calculatedPrice2, setCalculatedPrice2] = useState(0);
  const [calculatedPrice3, setCalculatedPrice3] = useState(0);
  const [calculatedPrice4, setCalculatedPrice4] = useState(0);
  const [calculatedPrice5, setCalculatedPrice5] = useState(0);

  const samplePrice = 18.99;

  const calculatePrice = (percentage) => {
    const percentValue = parseFloat(percentage);
    if (isNaN(percentValue)) return 0;
    return ((percentValue / 100) * samplePrice).toFixed(2);
  };

  const handlePercentageChange = (index, value) => {
    const newValue = value;
    const calculatedPrice = calculatePrice(newValue);

    switch (index) {
      case 1:
        setPercentage1(newValue);
        setCalculatedPrice1(calculatedPrice);
        break;
      case 2:
        setPercentage2(newValue);
        setCalculatedPrice2(calculatedPrice);
        break;
      case 3:
        setPercentage3(newValue);
        setCalculatedPrice3(calculatedPrice);
        break;
      case 4:
        setPercentage4(newValue);
        setCalculatedPrice4(calculatedPrice);
        break;
      case 5:
        setPercentage5(newValue);
        setCalculatedPrice5(calculatedPrice);
        break;
      default:
        break;
    }
  };

  const submitPriceVariationForm = async (event) => {
    event.preventDefault();
    const form = document.getElementById('addPriceVariation');
    const formData = new FormData(form);
    formData.append('sample_price', samplePrice);
    formData.append("user_id", userId);
    /*for (let i = 1; i <= 5; i++) {
      formData.append(`percentage${i}`, eval(`percentage${i}`));
      formData.append(`calculated_price${i}`, eval(`calculatedPrice${i}`));
    }*/
    setIsSubmitting(true);
    try {
      const settings = await getCompanySettings();
      const useEncrypt = resolveCustomerApiEncrypt(settings);
      let uploadBody;
      if (useEncrypt) {
        const plain = plainObjectFromFormData(formData);
        const encryptedPayload = encryptPayload(plain);
        if (!encryptedPayload) {
          setSnackbar({ open: true, message: "Encryption key missing.", severity: 'error' });
          setIsSubmitting(false);
          return;
        }
        uploadBody = new FormData();
        uploadBody.append("encrypted_payload", encryptedPayload);
      } else {
        uploadBody = formData;
      }
      const response = await axios.post(`${config.API_URL}/add_price_variation`, uploadBody, {
        headers: {
          authorization: `Bearer ${token}`,
        }
      });
      const result = normalizeEncryptedApiResponse(response.data, useEncrypt);
      if (result.status == "success") {
        setSnackbar({ open: true, message: "Price Variation added successfully.", severity: 'success' });
        setDialogOpen(false);
        setTimeout(() => {
          window.location.href = config.BASE_URL + '/products_price_variation/';
        }, 1500);
      } else {
        setSnackbar({ open: true, message: "Price Variation Failed. Try Again", severity: 'error' });
      }
    } catch {
    } finally {
      setIsSubmitting(false); // Re-enable the button
    }
  };

  return (
    <Box>
      <Dialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            p: 2
          }
        }}
      >
        <DialogTitle sx={{ pb: 2, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <Typography className="page-title">Price Variation</Typography>
          <IconButton onClick={() => setDialogOpen(false)} size="small">
            <Iconify icon="mdi:close" />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <Box component="form" id="addPriceVariation" sx={{ mt: 1 }}>
            <Grid2 container spacing={3}>
              <Grid2 size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  label="Sample Price"
                  name="pack"
                  id="pack"
                  value={samplePrice}
                  disabled
                  sx={{ mb: 3,
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#b5bdc9',
                      },
                    },
                  }}
                />
              </Grid2>
            </Grid2>

            {[...Array(5)].map((_, index) => {
              const idx = index + 1;
              const percentageValues = [percentage1, percentage2, percentage3, percentage4, percentage5];
              const calculatedPriceValues = [calculatedPrice1, calculatedPrice2, calculatedPrice3, calculatedPrice4, calculatedPrice5];
              return (
                <Grid2 container spacing={3} key={idx} sx={{ mb: 2 }}>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={`Enter Percentage for price ${idx + 1}`}
                      name={`percentage${idx}`}
                      value={percentageValues[idx - 1]}
                      onChange={(e) => handlePercentageChange(idx, e.target.value)}
                      required
                      className="form-input-border"
                      InputLabelProps={{
                        required: true
                      }}
                     
                    />
                  </Grid2>
                  <Grid2 size={{ xs: 12, sm: 6 }}>
                    <TextField
                      fullWidth
                      label={`Calculated Price ${idx + 1}`}
                      name={`calculated_price${idx}`}
                      value={calculatedPriceValues[idx - 1]}
                      disabled
                      className="form-input-border"
                    />
                  </Grid2>
                </Grid2>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button
            variant="outlined"
            className="button-variant-outlined"
            onClick={() => setDialogOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            className="button-variant-contained"
            onClick={submitPriceVariationForm}
            disabled={isSubmitting}
            id="submit"
            sx={{ fontSize: '14px', textTransform: 'none', fontWeight: 600 }}
          >
            {isSubmitting ? 'Submitting...' : 'Submit'}
          </Button>
        </DialogActions>
      </Dialog>


      <Box className="page-container">
        <Paper className="paper-style">
          <Box sx={{ px:2, py:1.2}}>
            <Grid2 container sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center'}} spacing={1}>
              <Grid2 size={{ xs: 12, md: 6 }}>
              <Typography className="page-title">
                Products Price Variation
              </Typography>
              </Grid2>
              <Grid2 size={{ xs: 12, md: 6 }} gap={1} display="flex" flexDirection={{ xs: 'column', md: 'row' }} justifyContent="flex-end">
              <TextField
                className="input-border"
                  size="small"
                  placeholder="Search price variations..."
                  value={searchInputValue}
                  onChange={handleSearchChange}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Iconify icon="eva:search-fill"/>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    width: { xs: '100%', sm: 400 },
                  }}
                />
              <Button
                variant="contained"
                className="button-variant-contained"
                onClick={() => setDialogOpen(true)}
                startIcon={<Iconify icon="mingcute:add-line" width={18} />}
                sx={{
                  borderRadius: '8px',
                  textTransform: 'none',
                  fontWeight: 500,
                  px: 1.2,
                  py: 0.6,
                  boxShadow: 'none',
                }}
              >
                Add Price
              </Button>
              </Grid2>
            </Grid2>
          </Box>
          <TableContainer
          >
            <Table size="small" className="table-cell-border" sx={{ tableLayout: 'auto', minWidth: 700 }}>
              <TableHead>
                <TableRow className="table-cell-header">
                  {HEADERS.map((header) => (
                    <TableCell key={header}>{header}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  [...Array(5)].map((_, i) => (
                    <TableRow key={`skeleton-${i}`}>
                      {HEADERS.map((_, j) => (
                        <TableCell key={j}>
                          <Skeleton variant="text" width="80%" />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={HEADERS.length} align="center" sx={{ py: 4, color: '#637381' }}>
                      No data
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((row, idx) => (
                    <TableRow 
                    className="table-cell-style"
                    key={row.id ?? idx} hover>
                      <TableCell>{row.date ?? ''}</TableCell>
                      <TableCell>{row.percentage2 ?? ''}</TableCell>
                      <TableCell>{row.percentage3 ?? ''}</TableCell>
                      <TableCell>{row.percentage4 ?? ''}</TableCell>
                      <TableCell>{row.percentage5 ?? ''}</TableCell>
                      <TableCell>{row.percentage6 ?? ''}</TableCell>
                      <TableCell>{row.username ?? ''}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
            component="div"
            count={totalRecords}
            page={page}
            onPageChange={handleChangePage}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            rowsPerPageOptions={[10, 25, 50, 100]}
            labelRowsPerPage="Rows per page:"
            className="pagination-styles"
          />
        </Paper>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={1500}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Products_Price_Variation;